import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LMSHeader from '../components/LMSHeader';

const HighSchoolLandingPage = () => {
    const [currentSlide, setCurrentSlide] = useState(0);

    // Hero slider content
    const heroSlides = [
        {
            title: "Empowering the Next Generation",
            subtitle: "STEM & Technical Skills for Nigerian Secondary Schools",
            image: "https://placehold.co/1920x600/1e40af/ffffff?text=Students+in+Lab"
        },
        {
            title: "Energy Education Starts Early",
            subtitle: "Oil & Gas Fundamentals for Senior Secondary Students",
            image: "https://placehold.co/1920x600/7c3aed/ffffff?text=Field+Visit"
        },
        {
            title: "Future-Ready Skills",
            subtitle: "Hands-on Training in Safety, Technology & Innovation",
            image: "https://placehold.co/1920x600/059669/ffffff?text=Robotics+Workshop"
        }
    ];

    // Trusted schools logos
    const trustedSchools = [
        { name: 'King’s College Lagos', logo: 'https://placehold.co/200x80/1e40af/ffffff?text=KCL' },
        { name: 'Queen’s College', logo: 'https://placehold.co/200x80/dc2626/ffffff?text=QC' },
        { name: 'Federal Government College', logo: 'https://placehold.co/200x80/0891b2/ffffff?text=FGC' },
        { name: 'St. Gregory’s College', logo: 'https://placehold.co/200x80/ea580c/ffffff?text=SGC' },
        { name: 'Anglican Girls’ Grammar', logo: 'https://placehold.co/200x80/7c3aed/ffffff?text=AGG' },
        { name: 'Command Science Secondary', logo: 'https://placehold.co/200x80/16a34a/ffffff?text=CSS' },
    ];

    // Training gallery images
    const galleryImages = [
        'https://placehold.co/400x300/1e40af/ffffff?text=Lab+Session',
        'https://placehold.co/400x300/7c3aed/ffffff?text=Field+Trip',
        'https://placehold.co/400x300/059669/ffffff?text=Workshop',
        'https://placehold.co/400x300/dc2626/ffffff?text=Certification',
        'https://placehold.co/400x300/0891b2/ffffff?text=STEM+Class',
        'https://placehold.co/400x300/ea580c/ffffff?text=Robotics'
    ];

    // Auto-slide hero
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <>
            <LMSHeader />
            {/* Hero Section with Slider */}
            <section className="relative h-[60vh] md:h-screen overflow-hidden">
                <div
                    className="flex transition-transform duration-1000 ease-in-out h-full"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                    {heroSlides.map((slide, i) => (
                        <div key={i} className="w-full flex-shrink-0 relative">
                            <img
                                src={slide.image}
                                alt={slide.title}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-center px-4">
                                <div className="max-w-4xl">
                                    <h1 className="text-3xl md:text-5xl font-bold mb-4">{slide.title}</h1>
                                    <p className="text-lg md:text-xl mb-6">{slide.subtitle}</p>
                                    <div className="space-x-4">
                                        <Link
                                            to="/school/register"
                                            className="inline-block px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg shadow-md"
                                        >
                                            Register Your School
                                        </Link>
                                        <Link
                                            to="/courses?category=high-school"
                                            className="inline-block px-6 py-3 border border-white text-white rounded-lg hover:bg-white hover:text-primary-600 transition"
                                        >
                                            Browse Courses
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Slide Indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {heroSlides.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentSlide(i)}
                            className={`w-3 h-3 rounded-full ${i === currentSlide ? 'bg-white' : 'bg-white bg-opacity-50'
                                }`}
                        ></button>
                    ))}
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {[{ number: "50+", label: "Schools Trained" }, { number: "5K+", label: "Students Empowered" }, { number: "92%", label: "Career Readiness Rate" }, { number: "₦120M+", label: "Total Value Delivered" }].map((stat, i) => (
                            <div key={i} className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-6 shadow-lg">
                                <div className="text-3xl md:text-4xl font-bold mb-1">{stat.number}</div>
                                <div className="text-sm md:text-base opacity-90">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 bg-neutral-50 dark:bg-neutral-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">
                            Why Partner With Us for High School Training?
                        </h2>
                        <p className="text-xl text-neutral-600 dark:text-neutral-400">
                            We equip students with foundational skills for careers in energy, technology, and engineering.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <FeatureCard
                            icon="🎓"
                            title="Curriculum-Aligned Programs"
                            description="Our courses complement national STEM and technical education standards."
                        />
                        <FeatureCard
                            icon="🔬"
                            title="Hands-On Learning"
                            description="From lab simulations to field visits — we make learning practical."
                        />
                        <FeatureCard
                            icon="📜"
                            title="Branded Certificates"
                            description="Certificates include school name and principal signature."
                        />
                        <FeatureCard
                            icon="👥"
                            title="Teacher-Led Workshops"
                            description="We train teachers to deliver our programs effectively."
                        />
                        <FeatureCard
                            icon="📊"
                            title="Progress Tracking Portal"
                            description="Real-time dashboards for principals and coordinators."
                        />
                        <FeatureCard
                            icon="💼"
                            title="Industry Career Pathways"
                            description="Prepare students for oil & gas, renewable energy, and tech jobs."
                        />
                    </div>
                </div>
            </section>

            {/* Trusted Schools Slider */}
            <ClientLogosSlider logos={trustedSchools} />

            {/* Gallery Section */}
            <section className="py-16 bg-white dark:bg-neutral-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-4">
                            Training in Action
                        </h2>
                        <p className="text-xl text-neutral-600 dark:text-neutral-400">
                            See how we’re transforming classrooms across Nigeria.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {galleryImages.map((img, i) => (
                            <div key={i} className="overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow">
                                <img
                                    src={img}
                                    alt={`Training session ${i + 1}`}
                                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-primary-50 dark:bg-primary-900/30">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-6">
                        Ready to Upskill Your Students?
                    </h2>
                    <p className="text-xl text-neutral-600 dark:text-neutral-400 mb-8">
                        Join hundreds of schools preparing youth for careers in Nigeria’s energy sector.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/school/register"
                            className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-medium text-lg rounded-lg shadow-lg transition-all duration-200"
                        >
                            Register Your School
                        </Link>
                        <Link
                            to="/contact"
                            className="px-8 py-4 bg-transparent border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-medium text-lg rounded-lg transition-all duration-200"
                        >
                            Request Demo
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer Call-to-Action */}
            <section className="py-16 bg-neutral-900 text-white">
                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold mb-4">Start Today</h2>
                    <p className="text-lg text-gray-300 mb-6">
                        Register your school and begin delivering world-class technical training to your students.
                    </p>
                    <Link
                        to="/school/register"
                        className="inline-block px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-medium text-lg rounded-lg shadow-lg transition"
                    >
                        Get Started
                    </Link>
                </div>
            </section>
        </>
    );
};

// Reusable Feature Card Component
const FeatureCard = ({ icon, title, description }) => (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-6 hover:shadow-md transition transform hover:-translate-y-1">
        <div className="text-5xl mb-4">{icon}</div>
        <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-3">{title}</h3>
        <p className="text-neutral-600 dark:text-neutral-400">{description}</p>
    </div>
);

// Client Logos Slider Component
const ClientLogosSlider = ({ logos }) => (
    <section className="py-16 bg-white dark:bg-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-center text-neutral-900 dark:text-white mb-12">
                Trusted by Leading Educational Institutions
            </h2>
            <div className="overflow-hidden">
                <div className="flex animate-scroll">
                    {logos.map((client, i) => (
                        <div key={i} className="flex-shrink-0 px-8 flex items-center justify-center">
                            <img
                                src={client.logo}
                                alt={client.name}
                                className="h-12 md:h-16 object-contain grayscale hover:grayscale-0 transition duration-300"
                            />
                        </div>
                    ))}
                    {/* Clone for seamless loop */}
                    {logos.map((client, i) => (
                        <div key={`clone-${i}`} className="flex-shrink-0 px-8 flex items-center justify-center">
                            <img
                                src={client.logo}
                                alt={client.name}
                                className="h-12 md:h-16 object-contain grayscale hover:grayscale-0 transition duration-300"
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>

        <style jsx>{`
      @keyframes scroll {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      .animate-scroll {
        display: flex;
        animation: scroll 30s linear infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        .animate-scroll {
          animation: none;
        }
      }
    `}</style>
    </section>
);

export default HighSchoolLandingPage;